import type { User } from '@auth0/auth0-vue'
import { createAuth0 } from '@auth0/auth0-vue'

/*
 * Browser-only Authorization Code + PKCE via Auth0's SPA SDK (ADR-0024),
 * mirroring apps/admin's own Auth0 integration (WFL-7541) rather than
 * @package/nuxt-auth0's bundled (UnoCSS-styled) pages — this layer ships
 * its own Nuxt UI login/callback/status pages under app/pages/auth/**.
 *
 * `skipRedirectCallback` on the callback route hands the redirect exchange
 * to that page's own `handleRedirectCallback()` call instead of auth0-vue's
 * automatic `install()`-time handling, so the callback page controls when
 * navigation happens. `errorPath` sends Auth0-rejected requests (bad
 * `?error=`) straight to /auth/status.
 */
export default defineNuxtPlugin({
  name: '001-auth0',
  parallel: true,
  setup: (nuxtApp) => {
    if (import.meta.server) return

    const config = nuxtApp.$config.public

    const plugin = createAuth0(
      {
        domain: config.AUTH0_DOMAIN,
        clientId: config.AUTH0_CLIENT_ID,
        cacheLocation: 'localstorage',
        authorizationParams: {
          redirect_uri: `${location.origin}/auth/callback`,
          audience: config.AUTH0_AUDIENCE,
        },
      },
      {
        errorPath: '/auth/status',
        skipRedirectCallback: location.pathname === '/auth/callback',
      },
    )
    nuxtApp.vueApp.use(plugin)

    const auth0 = () => plugin

    return {
      provide: {
        auth0,
        /* Resolves once the SDK has settled (session restored) or the
           timeout elapses, so the route guard reads isAuthenticated
           deterministically. Never rejects — a timeout just means "not
           authenticated yet". */
        auth0Ready: async ({
          timeout = 2000,
        }: { timeout?: number } = {}): Promise<User | undefined> => {
          const { isLoading, user } = auth0()
          if (user.value?.sub || !isLoading.value) return user.value

          await new Promise<void>((resolve) => {
            const timer = setTimeout(done, timeout)
            const stop = watch(isLoading, (loading) => {
              if (!loading) done()
            })
            function done() {
              clearTimeout(timer)
              stop()
              resolve()
            }
          })

          return user.value
        },
        loginWithRedirect: (email = '') => {
          auth0().loginWithRedirect({
            authorizationParams: email ? { login_hint: email } : {},
          })
        },
      },
    }
  },
})
